import { Box, Button, Grid, useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { AnalysisDetailContext } from "Components/Analyses/Detail/AnalysisDetailContext";
import { FamilyMemberDrawer } from "Components/Analyses/Family/FamilyMemberDrawer";
import { BlTooltip } from "Components/_Shared/BlTooltip";
import { ReactComponent as AddIcon } from "Components/_Shared/Icons/Assets/add.svg";
import { getGetAnalysisClientsQueryKey, useDeleteClient, useGetAnalysisClients } from "Infrastructure/ApiZfp/AnalyzerApi";
import { AnalysisClientItemAgeCategoryType, AnalysisClientItemRelationType } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { ChildTile } from "./ChildTile";

type Props = {
  readonly selectedClientId: string | undefined;
  readonly editMode?: boolean;
  readonly onSelect: (clientId: string) => void;
  readonly disabled?: boolean;
};

const CHILD_AGE_CATEGORIES: AnalysisClientItemAgeCategoryType[] = [
  AnalysisClientItemAgeCategoryType.child,
  AnalysisClientItemAgeCategoryType.plannedChild,
];

export const ChildrenManagement: React.FC<Props> = ({ selectedClientId, editMode, onSelect, disabled }) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { analysisId, versionId } = useParams() as {
    analysisId: string;
    versionId: string;
  };
  const { t } = useResource();
  const { canAddFamilyMember } = useContext(AnalysisDetailContext);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const { data } = useGetAnalysisClients(analysisId, versionId);

  useEffect(() => {
    if (!selectedClientId && data?.clients && data.clients.filter((c) => CHILD_AGE_CATEGORIES.includes(c.ageCategoryType)).length > 0) {
      onSelect(data.clients.filter((c) => CHILD_AGE_CATEGORIES.includes(c.ageCategoryType))[0].guid);
    }
  }, [data?.clients, onSelect, selectedClientId]);

  const { mutateAsync: deleteClient } = useDeleteClient({
    mutation: {
      onSuccess: () => {
        toast.success(t(Resources.AnalysisDetail.Toast.FamilyMemberDeleted));
        queryClient.invalidateQueries({ queryKey: getGetAnalysisClientsQueryKey(analysisId, versionId) });
      },
    },
  });

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  const handleClientDelete = async (clientId: string) => {
    try {
      if (!clientId) throw new Error();

      await deleteClient({
        clientGuid: clientId,
      });

      const first = data?.clients.find((client) => client.guid !== clientId && CHILD_AGE_CATEGORIES.includes(client.ageCategoryType));

      if (first) {
        onSelect(first.guid);
      }
    } catch {
      toast.error(t(Resources.AnalysisDetail.Toast.FamilyMemberDeleteError));
    }
  };

  return (
    <>
      <Grid container spacing={Spacing.L}>
        {data?.clients
          .filter((client) => CHILD_AGE_CATEGORIES.includes(client.ageCategoryType))
          .map((client) => {
            const selected = client.guid === selectedClientId;
            const disabledEdit = !selected && editMode;

            return (
              <Grid size={6} key={client.guid}>
                <ChildTile
                  clientId={client.guid}
                  firstName={client.firstName}
                  lastName={client.surname}
                  birthDate={client.birthDate ? new Date(client.birthDate) : undefined}
                  onEdit={() => handleDrawerOpen()}
                  onDelete={editMode ? undefined : () => handleClientDelete(client.guid)}
                  onSelect={onSelect}
                  disabled={disabledEdit || disabled}
                  selected={selected}
                />
              </Grid>
            );
          })}
        {!disabled && (
          <Grid size={12}>
            <BlTooltip title={canAddFamilyMember ? "" : t(Resources.AnalysisDetail.Family.AddMemberOwnerOnly)}>
              {/* a disabled button fires no pointer events, the tooltip listens on the wrapper */}
              <Box component="span" display="inline-flex">
                <Button disabled={!canAddFamilyMember} color="primary" variant="text" onClick={() => handleDrawerOpen()}>
                  <AddIcon color={canAddFamilyMember ? theme.palette.primary.main : theme.palette.text.disabled} />
                  &nbsp;{t(Resources.AnalysisDetail.Goals.AddExistingChild)}
                </Button>
              </Box>
            </BlTooltip>
          </Grid>
        )}
      </Grid>
      <FamilyMemberDrawer
        clientRelationType={AnalysisClientItemRelationType.RelatedClient}
        open={drawerOpen}
        anchor="right"
        onClose={handleDrawerClose}
      />
    </>
  );
};
